
import React from 'react';
import { GameState } from '../types';
import { RARITY_COLORS, MAX_JOKERS } from '../constants';
import JokerArt from './JokerArt';

interface HUDProps {
  gameState: GameState;
  onJokerClick: (joker: any) => void;
}

const HUD: React.FC<HUDProps> = ({ gameState, onJokerClick }) => {
  return (
    <div className="flex flex-row lg:flex-col w-full lg:w-72 gap-2 md:gap-4 z-10 animate-in fade-in slide-in-from-left duration-500 flex-shrink-0">
      <div className="hidden lg:flex flex-col gap-4 w-full">
        <div className="balatro-gradient p-3 md:p-5 rounded-2xl border-b-4 border-red-500/30 shadow-xl">
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Focus</span>
            <span className="text-xl md:text-2xl font-black text-red-500">{gameState.focus}/{gameState.maxFocus}</span>
          </div>
          <div className="flex gap-1 h-1.5 md:h-2">
            {Array.from({ length: gameState.maxFocus }).map((_, i) => (
              <div key={i} className={`flex-1 rounded-full ${i < gameState.focus ? 'bg-red-500 shadow-sm' : 'bg-white/5'}`} />
            ))}
          </div>
        </div>
        
        <div className="balatro-gradient p-3 md:p-5 rounded-2xl border-l-4 border-blue-500/30 shadow-xl">
          <div className="text-[10px] text-gray-500 uppercase font-black mb-1">Target</div>
          <div className="text-2xl md:text-4xl font-black tabular-nums leading-none">{gameState.score.toLocaleString()}</div>
          <div className="text-[10px] font-bold text-gray-700">/ {gameState.targetScore.toLocaleString()}</div>
          <div className="w-full h-1 bg-white/5 mt-3 md:mt-4 rounded-full overflow-hidden">
             <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${Math.min(100, (gameState.score / gameState.targetScore) * 100)}%` }} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 w-full">
        <div className="flex lg:grid lg:grid-cols-2 gap-2 w-full">
          <div className="balatro-gradient p-2 md:p-4 rounded-xl border-l-2 border-green-500 flex-1">
            <div className="text-[8px] md:text-[10px] text-gray-500 uppercase font-black">Gold</div>
            <div className="text-lg md:text-2xl font-black text-green-400">${gameState.money}</div>
          </div>
          <div className={`balatro-gradient p-2 md:p-4 rounded-xl border-l-2 border-[#ff0040] transition-all flex-1 ${gameState.streak >= 8 ? 'fire-glow' : ''}`}>
            <div className="text-[8px] md:text-[10px] text-gray-500 uppercase font-black">Streak</div>
            <div className={`text-lg md:text-2xl font-black ${gameState.streak >= 8 ? 'text-orange-500' : 'text-[#ff0040]'}`}>x{gameState.streak}</div>
          </div>
        </div>

        <div className="hidden lg:flex flex-col gap-2 mt-2">
          <div className="text-[10px] text-gray-600 uppercase font-black px-2 tracking-widest flex justify-between">
            <span>Power-Ups</span>
            <span className="opacity-40">{gameState.jokers.length}/{MAX_JOKERS}</span>
          </div>
          <div className="flex flex-col gap-2 overflow-y-auto max-h-[35vh] custom-scrollbar pr-2 min-h-0">
            {gameState.jokers.map(j => (
              <div 
                key={j.id} 
                onClick={() => onJokerClick(j)}
                className={`balatro-gradient p-1.5 rounded-xl border-2 transition-all flex items-center gap-3 cursor-pointer group hover:bg-white/[0.03] active:scale-95 ${RARITY_COLORS[j.rarity as keyof typeof RARITY_COLORS]} ${j.type === 'ACTIVE' ? 'active-pulse' : ''} ${j.type === 'ACTIVE' && j.currentUses === 0 ? 'opacity-30 grayscale' : ''}`}
              >
                <JokerArt id={j.id} rarity={j.rarity} />
                <div className="flex-1 overflow-hidden relative">
                  <div className="text-[10px] font-black uppercase truncate text-white leading-tight">
                    {j.name}
                  </div>
                  <div className={`text-[8px] font-bold uppercase tracking-tighter ${j.type === 'ACTIVE' ? 'text-blue-400' : 'text-slate-500'}`}>
                    {j.type} {j.usesPerPuzzle ? `[${j.currentUses}/${j.usesPerPuzzle}]` : ''}
                  </div>
                </div>
              </div>
            ))}
            {gameState.jokers.length === 0 && (
              <div className="text-center py-6 border-2 border-dashed border-white/5 rounded-xl opacity-20 text-[9px] uppercase font-black italic tracking-widest">
                LOADOUT EMPTY
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HUD;
